import { create } from 'zustand'
import { SSEManager, type SSEConnectionState, type CallEventData, type CallEventPayload } from '../api/sse'
import { useCopilotStore } from './copilotStore'

export type CallLifecycleStatus =
  | 'initiated'
  | 'ringing'
  | 'answered'
  | 'active'
  | 'voicemail'
  | 'cancelling'
  | 'ended'

export interface ActiveCall {
  call_sid: string
  direction: string
  mode: string
  from_number: string
  to_number: string
  started_at: number
  status: CallLifecycleStatus
  ended_by?: string
  ended_reason?: string
  recording_url?: string
  call_summary?: string
  cost_usd?: number
  cost_breakdown?: Record<string, unknown> | null
  listen_url?: string
  control_url?: string
  phone_call_provider_id?: string
}

export interface TelemetryEvent {
  event_type: string
  call_sid: string
  timestamp: number
  payload: CallEventPayload
}

interface TelemetryState {
  activeCalls: Record<string, ActiveCall>
  recentEvents: TelemetryEvent[]
  isConnected: boolean
  sseConnectionState: SSEConnectionState
  sseManager: SSEManager | null

  connect: () => void
  disconnect: () => void
  handleEvent: (eventType: string, data: CallEventData) => void
  registerPendingCall: (callSid: string, phoneNumber: string, mode?: string) => void
}

const MAX_EVENTS = 200

export const useTelemetryStore = create<TelemetryState>((set, get) => ({
  activeCalls: {},
  recentEvents: [],
  isConnected: false,
  sseConnectionState: 'disconnected',
  sseManager: null,

  connect: () => {
    const existing = get().sseManager
    if (existing) existing.disconnect()

    const manager = new SSEManager('/telemetry/stream/all')
    manager.subscribe((eventType, data) => {
      get().handleEvent(eventType, data)
    })
    manager.onStateChange((state) => {
      set({ sseConnectionState: state, isConnected: state === 'connected' })
    })
    manager.connect()

    set({
      sseManager: manager,
      sseConnectionState: 'connecting',
      activeCalls: {},
      recentEvents: [],
    })
  },

  disconnect: () => {
    const manager = get().sseManager
    if (manager) {
      manager.disconnect()
    }
    set({ sseManager: null, isConnected: false, sseConnectionState: 'disconnected' })
  },

  handleEvent: (eventType: string, data: CallEventData) => {
    const callSid = data.call_sid
    if (!callSid) return

    // Copilot events live in their own store so transcript/sentiment/cards
    // don't bloat the call-state tracker. Forward and return — they don't
    // affect activeCalls or recentEvents (recentEvents is bounded and used
    // for global "what just happened" UI; copilot transcripts would flood it).
    if (eventType.startsWith('copilot.')) {
      useCopilotStore.getState().handleEvent(eventType, data)
      return
    }

    const event: TelemetryEvent = {
      event_type: eventType,
      call_sid: callSid,
      timestamp: data.timestamp || Date.now() / 1000,
      payload: data.payload || data,
    }

    set((state) => {
      const updates: Partial<TelemetryState> = {}

      updates.recentEvents = [event, ...state.recentEvents].slice(0, MAX_EVENTS)

      switch (eventType) {
        case 'call.initiated': {
          const payload = data.payload || data
          updates.activeCalls = {
            ...state.activeCalls,
            [callSid]: {
              call_sid: callSid,
              direction: payload.direction || 'outbound',
              mode: payload.mode || 'pipeline',
              from_number: payload.from_number || 'unknown',
              to_number: payload.to_number || 'unknown',
              started_at: Date.now() / 1000,
              status: 'initiated',
              listen_url: payload.listen_url || undefined,
              control_url: payload.control_url || undefined,
              phone_call_provider_id: payload.phone_call_provider_id || undefined,
            },
          }
          break
        }

        case 'call.ringing': {
          const existing = state.activeCalls[callSid]
          const payload = data.payload || data
          if (existing) {
            updates.activeCalls = {
              ...state.activeCalls,
              [callSid]: {
                ...existing,
                status: 'ringing',
                from_number: payload.from_number || existing.from_number,
                to_number: payload.to_number || existing.to_number,
              },
            }
          } else {
            updates.activeCalls = {
              ...state.activeCalls,
              [callSid]: {
                call_sid: callSid,
                direction: payload.direction || 'unknown',
                mode: payload.mode || 'pipeline',
                from_number: payload.from_number || 'unknown',
                to_number: payload.to_number || 'unknown',
                started_at: Date.now() / 1000,
                status: 'ringing',
              },
            }
          }
          break
        }

        case 'call.answered': {
          const existing = state.activeCalls[callSid]
          const payload = data.payload || data
          if (existing) {
            updates.activeCalls = {
              ...state.activeCalls,
              [callSid]: {
                ...existing,
                status: 'answered',
                from_number: payload.from_number || existing.from_number,
                to_number: payload.to_number || existing.to_number,
              },
            }
          } else {
            updates.activeCalls = {
              ...state.activeCalls,
              [callSid]: {
                call_sid: callSid,
                direction: payload.direction || 'unknown',
                mode: payload.mode || 'pipeline',
                from_number: payload.from_number || 'unknown',
                to_number: payload.to_number || 'unknown',
                started_at: Date.now() / 1000,
                status: 'answered',
              },
            }
          }
          break
        }

        case 'call.started': {
          const payload = data.payload || data
          const existing = state.activeCalls[callSid]
          if (existing && (existing.status === 'ended' || existing.status === 'cancelling')) {
            break
          }
          updates.activeCalls = {
            ...state.activeCalls,
            [callSid]: {
              ...(existing || {}),
              call_sid: callSid,
              direction: payload.direction || existing?.direction || 'unknown',
              mode: payload.mode || existing?.mode || 'pipeline',
              from_number: payload.from_number || existing?.from_number || 'unknown',
              to_number: payload.to_number || existing?.to_number || 'unknown',
              started_at: payload.started_at || existing?.started_at || Date.now() / 1000,
              status: 'active' as CallLifecycleStatus,
            },
          }
          break
        }

        case 'call.cancelling': {
          const existing = state.activeCalls[callSid]
          if (existing && existing.status !== 'ended') {
            updates.activeCalls = {
              ...state.activeCalls,
              [callSid]: { ...existing, status: 'cancelling' },
            }
            setTimeout(() => {
              const s = get()
              const call = s.activeCalls[callSid]
              if (call && call.status === 'cancelling') {
                s.handleEvent('call.ended', {
                  call_sid: callSid,
                  payload: { ended_by: 'system', ended_reason: 'cancelling_timeout' },
                })
              }
            }, 15_000)
          }
          break
        }

        case 'call.ended': {
          const payload = data.payload || data
          const existing = state.activeCalls[callSid]
          if (existing) {
            if (existing.status === 'ended') {
              if (payload.ended_by === 'vapi') {
                updates.activeCalls = {
                  ...state.activeCalls,
                  [callSid]: {
                    ...existing,
                    ended_by: payload.ended_by,
                    ended_reason: payload.ended_reason,
                  },
                }
              }
              break
            }
            updates.activeCalls = {
              ...state.activeCalls,
              [callSid]: {
                ...existing,
                status: 'ended',
                ended_by: payload.ended_by,
                ended_reason: payload.ended_reason,
              },
            }
          } else {
            const remaining = { ...state.activeCalls }
            delete remaining[callSid]
            updates.activeCalls = remaining
          }

          setTimeout(() => {
            set((s) => {
              const remaining = { ...s.activeCalls }
              delete remaining[callSid]
              return { activeCalls: remaining }
            })
          }, 3_000)

          break
        }

        case 'call.report': {
          const payload = data.payload || data
          const existing = state.activeCalls[callSid]
          if (existing) {
            updates.activeCalls = {
              ...state.activeCalls,
              [callSid]: {
                ...existing,
                recording_url: payload.recording_url || undefined,
                call_summary: payload.summary || undefined,
                cost_usd: payload.cost_usd || undefined,
                cost_breakdown: payload.cost_breakdown || undefined,
              },
            }
          }
          break
        }

        case 'call.voicemail_detected': {
          const existing = state.activeCalls[callSid]
          if (existing) {
            updates.activeCalls = {
              ...state.activeCalls,
              [callSid]: { ...existing, status: 'voicemail' },
            }
          }
          break
        }
      }

      return updates
    })
  },

  registerPendingCall: (callSid: string, phoneNumber: string, mode?: string) => {
    set((state) => ({
      activeCalls: {
        ...state.activeCalls,
        [callSid]: {
          call_sid: callSid,
          direction: 'outbound',
          mode: mode || 'pipeline',
          from_number: 'unknown',
          to_number: phoneNumber,
          started_at: Date.now() / 1000,
          status: 'initiated',
        },
      },
    }))
  },
}))
